import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Shield, TreePine, Users, Building, ArrowRight, Mail } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const HERO_IMG = "https://customer-assets.emergentagent.com/job_protect-florida-now/artifacts/cl1mvr0p_68A47ABF-877E-44DF-8BE0-14B5CF9CB3CB.PNG";
const ABOUT_IMG = "https://customer-assets.emergentagent.com/job_protect-florida-now/artifacts/rcbvjapp_D35BB94A-6F16-417E-8EBA-069AD1D48703.PNG";
const NATURE_IMG = "https://images.unsplash.com/photo-1616919954110-a94268e70f6d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzd8MHwxfHNlYXJjaHwyfHxmbG9yaWRhJTIwbmF0dXJlJTIwbGFuZHNjYXBlJTIwc3VucmlzZXxlbnwwfHx8fDE3NzYxNzk1NDZ8MA&ixlib=rb-4.1.0&q=85&w=800";

const programs = [
  { title: 'Membership', desc: 'Join the movement starting at $10/month. Five tiers available.', path: '/programs/membership', icon: Users },
  { title: 'Seat at the Table', desc: 'A voice for our land, our wildlife, and our waters.', path: '/programs/seat-at-the-table', icon: Shield },
  { title: 'Legal Defense Fund', desc: 'Fund legal action for Florida communities.', path: '/programs/legal-defense-fund', icon: Building },
  { title: 'Priority Zones', desc: 'Strategic land preservation mapping.', path: '/programs/priority-zones', icon: TreePine },
];

function NewsletterSection() {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      await axios.post(`${API}/newsletter/subscribe`, { email, name });
      toast.success('Successfully subscribed to the newsletter!');
      setEmail(''); setName('');
    } catch {
      toast.error('Subscription failed. Please try again.');
    } finally { setLoading(false); }
  };

  return (
    <section className="py-20 bg-navy" data-testid="newsletter-section">
      <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
        <Mail className="w-10 h-10 text-gold mx-auto mb-4" />
        <h2 className="text-3xl font-heading font-bold text-white mb-3">Stay Informed</h2>
        <p className="text-base text-white/80 font-body mb-8">Subscribe to the Safe Florida newsletter for community updates, policy alerts, and event invitations.</p>
        <form onSubmit={handleSubscribe} className="flex flex-col sm:flex-row gap-3 max-w-lg mx-auto">
          <Input value={name} onChange={e => setName(e.target.value)} placeholder="Your name" className="bg-white/10 border-white/20 text-white placeholder:text-white/50 font-body" data-testid="newsletter-name-input" />
          <Input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="Your email" required className="bg-white/10 border-white/20 text-white placeholder:text-white/50 font-body" data-testid="newsletter-email-input" />
          <Button type="submit" disabled={loading} className="bg-gold text-navy hover:bg-gold/90 font-body font-bold px-6 whitespace-nowrap" data-testid="newsletter-subscribe-btn">
            {loading ? 'Subscribing...' : 'Subscribe'}
          </Button>
        </form>
        <p className="text-xs text-white/50 font-body mt-4">#SafeFlorida #SupportLocalBusiness #EconomicAccountability #TransparencyMatters</p>
      </div>
    </section>
  );
}

export default function Home() {
  return (
    <div data-testid="home-page">
      {/* Hero Banner Section */}
      <section className="pt-24 pb-10 sm:pt-28 sm:pb-14 bg-[#FAFAFA]" data-testid="hero-section">
        <div className="hero-banner max-w-[1200px] mx-auto px-4">
          <img
            src={HERO_IMG}
            alt="Safe Florida - Protecting Families. Securing Land. Strengthening Communities. A Community Membership Movement."
            className="w-full h-auto block rounded-xl"
            style={{ boxShadow: '0 4px 14px rgba(0,0,0,0.08)' }}
            data-testid="hero-banner-image"
          />
        </div>
        <div className="max-w-4xl mx-auto px-6 text-center mt-10">
          <div className="flex flex-wrap gap-4 justify-center">
            <Link to="/programs/membership" data-testid="hero-cta-become-member">
              <Button size="lg" className="btn-primary-safe rounded-md px-8 py-3 font-body font-bold text-sm tracking-wide">
                BECOME A MEMBER
              </Button>
            </Link>
            <Link to="/programs" data-testid="hero-cta-see-membership">
              <Button size="lg" variant="outline" className="rounded-md px-8 py-3 font-body font-bold text-sm tracking-wide border-navy text-navy hover:bg-navy/5">
                SEE WHAT MEMBERSHIP INCLUDES
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Mission Statement */}
      <section className="py-24 bg-white" data-testid="mission-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <p className="text-xs tracking-[0.2em] uppercase font-bold text-gold font-body mb-3">Our Mission</p>
              <h2 className="text-3xl sm:text-4xl font-heading font-bold text-navy mb-6 leading-tight">
                Building a Stronger Florida, Together
              </h2>
              <p className="text-base text-gray-700 font-body leading-relaxed mb-4">
                Safe Florida is a 501(c)(4) social welfare organization based in Port St. Lucie, Florida. We organize communities, fund legal action, preserve strategic land, and build membership-backed statewide influence.
              </p>
              <p className="text-base text-gray-700 font-body leading-relaxed mb-6">
                Under the leadership of F.C. Clark, our movement brings together families, veterans, and community members who believe in protecting Florida's future for generations to come.
              </p>
              <p className="font-signature text-gold text-3xl">F.C. Clark</p>
              <p className="text-xs text-gray-500 font-body mt-1">Founder & Leader, Safe Florida</p>
            </div>
            <div className="relative">
              <img src={ABOUT_IMG} alt="Safe Florida community - Veterans, families, and gold shield" className="rounded-lg w-full object-cover" style={{ boxShadow: '0 4px 14px rgba(0,0,0,0.08)' }} />
            </div>
          </div>
        </div>
      </section>

      {/* Programs Preview */}
      <section className="py-24 bg-[#FAFAFA]" data-testid="programs-preview-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="text-xs tracking-[0.2em] uppercase font-bold text-gold font-body mb-3">Our Programs</p>
            <h2 className="text-3xl sm:text-4xl font-heading font-bold text-navy mb-4">How We Make a Difference</h2>
            <p className="text-base text-gray-600 font-body max-w-2xl mx-auto">Join our programs and become part of a movement that protects, preserves, and strengthens Florida communities.</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {programs.map(({ title, desc, path, icon: Icon }) => (
              <Link key={title} to={path} className="card-hover bg-white border border-gray-200 rounded-lg p-8 group" data-testid={`program-card-${title.toLowerCase().replace(/\s+/g, '-')}`}>
                <div className="w-12 h-12 rounded-lg bg-navy/5 flex items-center justify-center mb-4 group-hover:bg-gold/10 transition-colors">
                  <Icon className="w-6 h-6 text-navy group-hover:text-gold transition-colors" />
                </div>
                <h3 className="font-heading font-bold text-navy text-lg mb-2">{title}</h3>
                <p className="text-sm text-gray-600 font-body leading-relaxed mb-4">{desc}</p>
                <span className="text-gold text-sm font-body font-medium inline-flex items-center gap-1 group-hover:gap-2 transition-all">
                  Learn More <ArrowRight className="w-4 h-4" />
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative py-24" data-testid="cta-section">
        <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${NATURE_IMG})` }} />
        <div className="absolute inset-0 bg-forest/80" />
        <div className="relative z-10 max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-heading font-bold text-white mb-6">Ready to Make a Difference?</h2>
          <p className="text-lg text-white/90 font-body mb-8">Join Safe Florida today and stand with families, veterans, and communities across the state.</p>
          <Link to="/programs/membership" data-testid="cta-join">
            <Button size="lg" className="bg-gold text-navy hover:bg-gold/90 font-body font-bold px-10 py-3 rounded-md">
              JOIN THE MOVEMENT
            </Button>
          </Link>
        </div>
      </section>

      {/* Newsletter Signup */}
      <NewsletterSection />
    </div>
  );
}
